import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../../_services/busy.service';
import { GlobalService } from '../../_services/global.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';
import { tag_returndata } from 'src/types/tag_returndata';
import { tag } from 'src/types/tag';

//One admission type as a column on the report. The key is what the row is indexed by, because an
//admission's name can hold anything - a dot in it would be read as a path by the table.
interface admissioncolumn {
  key: string;
  name: string;
  total: number;
}

//An admission's price as set on its package. Per Booking ones (fixedPrice) are charged once
//however many people they cover.
interface admissionprice {
  price: number;
  perBooking: boolean;
}

//The Admission Report laid out the same way - a column per admission type - but each column holds
//what that admission was worth on the booking (how many of it x its price) rather than the count.
@Component({
  selector: 'app-admissionvaluereport',
  templateUrl: './admissionvaluereport.component.html',
  styleUrls: ['./admissionvaluereport.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdmissionvaluereportComponent implements OnInit {

  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  CurrencySymbol: string = localStorage.getItem("Currency") == null ? "GBP" : localStorage.getItem("Currency")!;

  FromDate: Date = new Date();
  ToDate: Date = new Date();

  tags: tag[] = [];
  chosentags: tag[] = [];

  ListBookings: any[] = [];
  //One per admission type found in the period, so a venue only sees the ones it actually sells.
  admissionColumns: admissioncolumn[] = [];

  //Every package's admission prices, read once. Disabled packages are included, as a booking made
  //on one still needs pricing.
  private packages: any[] = null;

  //Admissions on the period's bookings that no price could be found for - a package or admission
  //renamed or removed since. Shown on the page so a short total is not taken at face value.
  unpriced: number = 0;

  guests: number = 0;
  admissionsValue: number = 0;
  total: number = 0;
  outstanding: number = 0;

  cols: any[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService) { }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        this.AccessAllowed = getAccess("REPORTS", this.httpResponse.data) == true;

        this.loadTags();
        this.loadgrid();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  loadTags() {
    this.http.get(bmxurl + "TagList/" + this.VenueID + "/false").subscribe(response => {
      this.tags = (response as tag_returndata).data;
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Tags.'});
    });
  }

  doneDate() {
    this.loadgrid();
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  loadgrid() {
    this.busyService.busy();

    let searchCrit = {
      venueid: this.VenueID,
      startdate: ConvertDateToDB(this.FromDate),
      enddate: ConvertDateToDB(this.ToDate),
      tags: this.chosentags,
      disabled: false,
      created: true
    };

    //The packages are only fetched the first time; a failure there still lets the bookings show.
    const packages$ = this.packages != null
      ? of(this.packages)
      : this.http.get<any>(bmxurl + "packagelist/" + this.VenueID + "/true").pipe(catchError(() => of(null)));

    forkJoin({
      bookings: this.http.post(bmxurl + "/BookingList", searchCrit),
      packages: packages$
    }).subscribe(result => {
      if (this.packages == null) {
        const list = Array.isArray(result.packages) ? result.packages : (result.packages?.data ?? null);

        if (list == null) {
          this.messageService.add({severity:'warn', summary:'Prices unavailable', detail:'The package prices could not be read, so admission values cannot be worked out.'});
        }

        this.packages = list ?? [];
      }

      const bookings = ((result.bookings as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false);

      this.buildRows(bookings);
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  private buildRows(bookings: any[]) {
    this.guests = 0;
    this.admissionsValue = 0;
    this.total = 0;
    this.outstanding = 0;
    this.unpriced = 0;

    //Every admission type seen in the period, in the order it was first met, so the columns do
    //not jump about between runs of the same report.
    const columns: admissioncolumn[] = [];

    const columnFor = (name: string): admissioncolumn => {
      let found = columns.find(column => column.name == name);

      if (found == null) {
        found = { key: "adm" + columns.length, name: name, total: 0 };
        columns.push(found);
      }

      return found;
    };

    bookings.forEach(booking => {
      booking.startTimeCSV = this.formatDateForExcel(booking.startTime);
      booking.createddateCSV = this.formatDateForExcel(booking.createddate);

      this.guests += Number(booking.guests) || 0;
      this.total += Number(booking.total) || 0;
      this.outstanding += Number(booking.outstanding) || 0;

      let bookingValue = 0;

      this.admissionsOn(booking).forEach(adm => {
        //The column is there whether or not a price is found, exactly as on the Admission Report.
        const column = columnFor(adm.name);
        const value = this.valueOf(booking, adm);

        if (value == null) {
          this.unpriced++;
          return;
        }

        //Put straight on the row, which is what the table and the CSV export both read.
        booking[column.key] = this.roundMoney((Number(booking[column.key]) || 0) + value);
        column.total = this.roundMoney(column.total + value);
        bookingValue += value;
      });

      booking.admissionsvalue = this.roundMoney(bookingValue);
      this.admissionsValue += bookingValue;
    });

    this.admissionsValue = this.roundMoney(this.admissionsValue);
    this.total = this.roundMoney(this.total);
    this.outstanding = this.roundMoney(this.outstanding);

    this.admissionColumns = columns;
    this.ListBookings = bookings;

    this.cols = [
      { field: 'bookingIdHR', header: 'Sales ID' },
      { field: 'startTimeCSV', header: 'Start Date/Time' },
      { field: 'name', header: 'Name' },
      { field: 'email', header: 'Email' },
      { field: 'phone', header: 'Phone' },
      { field: 'packageName', header: 'Package' },
      ...columns.map(column => ({ field: column.key, header: column.name })),
      { field: 'admissionsvalue', header: 'Admissions Value' },
      { field: 'guests', header: 'Guests' },
      { field: 'total', header: 'Total' },
      { field: 'outstanding', header: 'Outstanding' },
    ];
  }

  //What one admission on a booking was worth: how many of it times its price, or its price once for
  //a Per Booking admission. The booking's own price is used when it carries one; otherwise the
  //admission's price on the booking's package. Null when no price can be found.
  private valueOf(booking: any, adm: { name: string, people: number, price: number, perBooking: boolean }): number {
    let price = adm.price;
    let perBooking = adm.perBooking;

    if (price == null) {
      const fromPackage = this.packagePriceFor(booking, adm.name);

      if (fromPackage == null) {
        return null;
      }

      price = fromPackage.price;
      perBooking = fromPackage.perBooking;
    }

    return perBooking ? price : adm.people * price;
  }

  //The admission's price on the booking's package, found by the package's id and then its name, and
  //the admission by its name - the booking list only gives the admission's name.
  private packagePriceFor(booking: any, admissionName: string): admissionprice {
    const packageId = booking.packageId ?? booking.packageid;
    const packageName = (booking.packageName ?? booking.packagename ?? "").toString().trim().toLowerCase();

    const pkg = (packageId != null ? this.packages.find(item => item.packageId == packageId) : null)
      ?? this.packages.find(item => (item.name ?? "").toString().trim().toLowerCase() == packageName);

    const wanted = admissionName.trim().toLowerCase();
    const adm = (pkg?.admissions ?? []).find((item: any) => (item.name ?? "").toString().trim().toLowerCase() == wanted);

    if (adm == null || adm.price == null || isNaN(Number(adm.price))) {
      return null;
    }

    return { price: Number(adm.price), perBooking: adm.fixedPrice == true };
  }

  //A booking's admissions arrive either as the objects themselves or as the line the API writes
  //for display - "2 x Adults, 1 x Child" - so both are read. Only the objects can carry a price.
  private admissionsOn(booking: any): { name: string, people: number, price: number, perBooking: boolean }[] {
    if (Array.isArray(booking.admissions)) {
      return booking.admissions
        .filter((adm: any) => (Number(adm.noofpeople) || 0) > 0)
        .map((adm: any) => ({
          name: (adm.name ?? "").toString().trim(),
          people: Number(adm.noofpeople) || 0,
          price: adm.price != null && !isNaN(Number(adm.price)) ? Number(adm.price) : null,
          perBooking: adm.fixedPrice == true
        }))
        .filter((adm: any) => adm.name != "");
    }

    if (typeof booking.admissions != "string" || booking.admissions.trim() == "") {
      return [];
    }

    return booking.admissions
      .split(",")
      .map((part: string) => part.trim())
      .filter((part: string) => part != "")
      .map((part: string) => {
        const split = part.match(/^(\d+)\s*x\s*(.+)$/i);

        //Anything that is not "N x Name" is counted as one of itself rather than thrown away.
        return split == null
          ? { name: part, people: 1, price: null, perBooking: false }
          : { name: split[2].trim(), people: Number(split[1]) || 0, price: null, perBooking: false };
      })
      .filter((adm: any) => adm.name != "" && adm.people > 0);
  }

  private roundMoney(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }

  formatDateForExcel(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = ('0' + d.getUTCDate()).slice(-2);
    const month = ('0' + (d.getUTCMonth() + 1)).slice(-2);
    const year = d.getUTCFullYear();
    const hours = ('0' + d.getUTCHours()).slice(-2);
    const minutes = ('0' + d.getUTCMinutes()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  //The period cannot run backwards. Moving the From date past the To date takes the To date with
  //it, and the To picker will not open on anything earlier than the From date.
  fromDateChanged() {
    if (this.FromDate == null || this.ToDate == null) {
      return;
    }

    if (new Date(this.ToDate) < new Date(this.FromDate)) {
      this.ToDate = new Date(this.FromDate);
    }
  }
}
